import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ClearIcon from "@mui/icons-material/Clear";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import { useBaseDatosListState } from "../../components/BaseDatosLayout";
import {
  deleteSaleProduct,
  getSaleProducts,
  updateSaleProductActive,
} from "../api/saleProductsApi";
import SaleProductsTable from "../components/SaleProductsTable";
import SaleProductContentModal from "../components/SaleProductContentModal";
import SaleProductActiveDialog from "../components/SaleProductActiveDialog";
import SaleProductDeleteDialog from "../components/SaleProductDeleteDialog";
import {
  isSaleProductCostUsable,
  saleProductCostStatusLabel,
  saleProductReviewReasonsLabel,
} from "../utils/saleProductStatus.utils";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 700;
const SECTORS = ["Cocina", "Barra"];

const selectSx = {
  width: { xs: "100%", sm: 150 },
  height: 38,
  borderRadius: "33px",
  bgcolor: "neutral.100",
  color: "primary.900",
  fontSize: 13,
  fontWeight: 700,
  boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "secondary.500" },
  "& .MuiSelect-select": { display: "flex", alignItems: "center", py: 0 },
};

function FilterSelect({ value, onValueChange, placeholder, getLabel, children }) {
  return (
    <Select
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      displayEmpty
      size="small"
      IconComponent={value ? () => null : undefined}
      sx={{
        ...selectSx,
        "& .MuiSelect-select": {
          ...selectSx["& .MuiSelect-select"],
          pr: value ? "32px !important" : undefined,
        },
      }}
      renderValue={(selectedValue) =>
        selectedValue ? getLabel?.(selectedValue) || selectedValue : placeholder
      }
      endAdornment={
        value ? (
          <InputAdornment
            position="end"
            sx={{ position: "absolute", right: 8, color: "primary.900" }}
          >
            <IconButton
              size="small"
              aria-label={`Limpiar filtro ${placeholder.toLowerCase()}`}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={(event) => {
                event.stopPropagation();
                onValueChange("");
              }}
              sx={{ width: 24, height: 24, opacity: 0.7, "&:hover": { opacity: 1 } }}
            >
              <ClearIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </InputAdornment>
        ) : null
      }
    >
      {children}
    </Select>
  );
}

function CardShell({ children }) {
  return (
    <Box
      sx={{
        p: 2,
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
        bgcolor: "neutral.300",
        borderRadius: "30px",
        boxShadow: "inset 0px 0px 4px rgba(117, 117, 138, 0.5)",
      }}
    >
      {children}
    </Box>
  );
}

function unwrapHttp(response) {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    !("meta" in response)
  ) {
    return response.data;
  }
  return response;
}

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
}

function formatSectors(value) {
  const sectors = Array.isArray(value)
    ? value
    : typeof value === "string"
    ? value.split(",")
    : [];
  const normalized = SECTORS.filter((sector) =>
    sectors.some(
      (item) =>
        String(item).trim().toLocaleLowerCase("es-AR") ===
        sector.toLocaleLowerCase("es-AR")
    )
  );
  return normalized.length ? normalized.join(" y ") : "—";
}

function normalizeSaleProduct(product) {
  if (!product?.id) return null;
  const summary = product.contentSummary || {};
  const ingredientCount = Number(summary.ingredientCount) || 0;
  const preparedProductCount = Number(summary.preparedProductCount) || 0;
  const recipeCount = Number(summary.recipeCount) || 0;
  const totalComponents =
    Number(summary.totalComponents) ||
    ingredientCount + preparedProductCount + recipeCount;
  const isActive = Boolean(product.isActive);
  const costStatus = String(product.costStatus || "REQUIRES_REVIEW");
  const isAvailable =
    isActive &&
    (typeof product.isAvailable === "boolean"
      ? product.isAvailable
      : isSaleProductCostUsable(costStatus));
  const reasons = Array.isArray(product.costReviewReasons)
    ? product.costReviewReasons
    : [];

  return {
    ...product,
    id: Number(product.id),
    name: String(product.name || "Sin nombre"),
    sectorLabel: formatSectors(product.sector),
    priceLabel: formatCurrency(product.price),
    costLabel:
      costStatus === "ESTIMATED"
        ? formatCurrency(product.cost)
        : costStatus === "INCOMPLETE"
        ? "Estimación incompleta"
        : costStatus === "UNAVAILABLE"
        ? "Sin costo estimado"
        : costStatus === "REQUIRES_REVIEW"
        ? "Requiere revisión"
        : product.costCalculatedAt
        ? formatCurrency(product.cost)
        : "Pendiente",
    ingredientCount,
    preparedProductCount,
    recipeCount,
    totalComponents,
    contentDetail: `${ingredientCount} insumos · ${preparedProductCount} elaborados · ${recipeCount} recetas`,
    isActive,
    costStatus,
    isAvailable,
    availabilityLabel: isAvailable
      ? "Disponible"
      : costStatus === "REQUIRES_REVIEW"
      ? "Requiere revisión"
      : "No disponible",
    costReviewReasonsLabel: saleProductReviewReasonsLabel(reasons),
  };
}

function errorToMessage(error) {
  return (
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message ||
    "No se pudieron cargar los productos de venta."
  );
}

export default function ProductosVentaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const latestRequestId = useRef(0);
  const [remembered, remember] = useBaseDatosListState("productos", {});
  const initialListState = useRef(location.state?.listState || remembered || {}).current;
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(() => Number(initialListState.page) || 1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [nameInput, setNameInput] = useState(initialListState.nameInput || "");
  const [nameFilter, setNameFilter] = useState(initialListState.nameFilter || "");
  const [sectorFilter, setSectorFilter] = useState(initialListState.sectorFilter || "");
  const [activeFilter, setActiveFilter] = useState(initialListState.activeFilter || "");
  const [costStatusFilter, setCostStatusFilter] = useState(initialListState.costStatusFilter || "");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.successMessage || ""
  );
  const [contentProduct, setContentProduct] = useState(null);
  const [deactivatingProduct, setDeactivatingProduct] = useState(null);
  const [productToDelete, setProductToDelete] = useState(null);
  const [updatingProductId, setUpdatingProductId] = useState(null);
  const [deletingProductId, setDeletingProductId] = useState(null);
  const [operationError, setOperationError] = useState("");
  const [deleteError, setDeleteError] = useState("");
  const [reloadVersion, setReloadVersion] = useState(0);

  useEffect(() => {
    if (!location.state?.successMessage) return;
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state?.successMessage, navigate]);

  const fetchProducts = useCallback(
    async (signal) => {
      const requestId = ++latestRequestId.current;
      setLoading(true);
      setError("");
      try {
        const response = await getSaleProducts({
          page,
          limit: PAGE_SIZE,
          name: nameFilter || undefined,
          sector: sectorFilter || undefined,
          isActive: activeFilter || undefined,
          costStatus: costStatusFilter || undefined,
          signal,
        });
        const payload = unwrapHttp(response);
        if (signal?.aborted || requestId !== latestRequestId.current) return;

        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const normalized = list.map(normalizeSaleProduct).filter(Boolean);
        const responseTotalPages =
          Number(payload?.meta?.totalPages) || (normalized.length ? 1 : 0);

        if (responseTotalPages > 0 && page > responseTotalPages) {
          setPage(responseTotalPages);
          return;
        }

        setProducts(normalized);
        setTotalItems(Number(payload?.meta?.totalItems) || normalized.length);
        setTotalPages(responseTotalPages);
      } catch (requestError) {
        if (signal?.aborted || requestId !== latestRequestId.current) return;
        setProducts([]);
        setTotalItems(0);
        setTotalPages(0);
        setError(errorToMessage(requestError));
      } finally {
        if (!signal?.aborted && requestId === latestRequestId.current) {
          setLoading(false);
        }
      }
    },
    [activeFilter, costStatusFilter, nameFilter, page, sectorFilter]
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchProducts(controller.signal);
    return () => controller.abort();
  }, [fetchProducts, reloadVersion]);

  const commitNameFilter = useCallback(
    (value) => {
      const normalized = String(value ?? "").trim();
      if (normalized === nameFilter) return;
      setNameFilter(normalized);
      setPage(1);
    },
    [nameFilter]
  );

  useEffect(() => {
    const timeout = setTimeout(() => commitNameFilter(nameInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [commitNameFilter, nameInput]);

  const pageSummary = useMemo(() => {
    if (!totalItems) return "Sin productos de venta para mostrar";
    return `Mostrando ${products.length} de ${totalItems} productos de venta`;
  }, [products.length, totalItems]);

  const hasFilters = Boolean(
    nameFilter || sectorFilter || activeFilter || costStatusFilter
  );
  const listState = useMemo(
    () => ({
      page,
      nameInput,
      nameFilter,
      sectorFilter,
      activeFilter,
      costStatusFilter,
    }),
    [activeFilter, costStatusFilter, nameFilter, nameInput, page, sectorFilter]
  );

  useEffect(() => {
    remember(listState);
  }, [listState, remember]);

  function updateFilter(setter, value) {
    setter(value);
    setPage(1);
  }

  async function changeActive(product, isActive) {
    if (!product?.id || updatingProductId !== null) return;
    setUpdatingProductId(product.id);
    setOperationError("");
    try {
      await updateSaleProductActive(product.id, isActive);
      setDeactivatingProduct(null);
      setReloadVersion((value) => value + 1);
    } catch (requestError) {
      setOperationError(
        errorToMessage(requestError) || "No se pudo actualizar el estado del producto."
      );
      setReloadVersion((value) => value + 1);
    } finally {
      setUpdatingProductId(null);
    }
  }

  function requestActiveChange(product) {
    if (product.isActive) {
      setOperationError("");
      setDeactivatingProduct(product);
      return;
    }
    changeActive(product, true);
  }

  function requestDelete(product) {
    if (!product?.id || deletingProductId !== null) return;
    setDeleteError("");
    setProductToDelete(product);
  }

  async function confirmDelete() {
    if (!productToDelete?.id || deletingProductId !== null) return;
    const productId = productToDelete.id;
    setDeletingProductId(productId);
    setDeleteError("");
    try {
      await deleteSaleProduct(productId);
      setProductToDelete(null);
      setReloadVersion((value) => value + 1);
    } catch (requestError) {
      setDeleteError(
        errorToMessage(requestError) || "No se pudo eliminar el producto."
      );
    } finally {
      setDeletingProductId(null);
    }
  }

  return (
    <Stack
      spacing={2}
      sx={{ p: 2, flex: 1, minHeight: 0, overflow: { xs: "auto", md: "hidden" } }}
    >
      <StockPageHeader
        title="Productos de venta"
        onBack={() => navigate("/stock")}
      />

      <CardShell>
        <Stack spacing={1.5} sx={{ height: "100%", minHeight: 0 }}>
          <Stack
            direction={{ xs: "column", lg: "row" }}
            spacing={1.5}
            sx={{ alignItems: { xs: "stretch", lg: "center" }, justifyContent: "space-between" }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.1}
              sx={{ alignItems: { xs: "stretch", sm: "center" }, flexWrap: "wrap" }}
            >
              <FilterSelect
                value={sectorFilter}
                onValueChange={(value) => updateFilter(setSectorFilter, value)}
                placeholder="Sector"
              >
                <MenuItem value="">Todos los sectores</MenuItem>
                {SECTORS.map((sector) => (
                  <MenuItem key={sector} value={sector}>{sector}</MenuItem>
                ))}
              </FilterSelect>
              <FilterSelect
                value={activeFilter}
                onValueChange={(value) => updateFilter(setActiveFilter, value)}
                placeholder="Estado"
                getLabel={(value) => (value === "true" ? "Activo" : "Inactivo")}
              >
                <MenuItem value="">Todos los estados</MenuItem>
                <MenuItem value="true">Activo</MenuItem>
                <MenuItem value="false">Inactivo</MenuItem>
              </FilterSelect>
              <FilterSelect
                value={costStatusFilter}
                onValueChange={(value) => updateFilter(setCostStatusFilter, value)}
                placeholder="Estado de costo"
                getLabel={saleProductCostStatusLabel}
              >
                <MenuItem value="">Todos los costos</MenuItem>
                <MenuItem value="ESTIMATED">Estimado</MenuItem>
                <MenuItem value="INCOMPLETE">Estimación incompleta</MenuItem>
                <MenuItem value="UNAVAILABLE">Sin costo estimado</MenuItem>
                <MenuItem value="VALID">Válido (histórico)</MenuItem>
                <MenuItem value="REQUIRES_REVIEW">Requiere revisión</MenuItem>
              </FilterSelect>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.2}
              sx={{ alignItems: { xs: "stretch", sm: "center" } }}
            >
              <TextField
                value={nameInput}
                onChange={(event) => setNameInput(event.target.value)}
                onBlur={(event) => commitNameFilter(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") return;
                  event.preventDefault();
                  commitNameFilter(nameInput);
                }}
                placeholder="Buscar"
                size="small"
                slotProps={{ htmlInput: { maxLength: 200 } }}
                sx={{
                  width: { xs: "100%", md: 220 },
                  "& .MuiOutlinedInput-root": {
                    height: 38,
                    borderRadius: "33px",
                    bgcolor: "neutral.50",
                    color: "primary.900",
                    fontSize: 13,
                    fontWeight: 600,
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    "& fieldset": { border: "none" },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "primary.900", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  endAdornment: nameInput ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label="Limpiar búsqueda"
                        onClick={() => {
                          setNameInput("");
                          setNameFilter("");
                          setPage(1);
                        }}
                        sx={{ width: 24, height: 24, color: "primary.900", opacity: 0.7 }}
                      >
                        <ClearIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Button
                type="button"
                variant="outlined"
                startIcon={<MenuBookIcon sx={{ fontSize: 18 }} />}
                onClick={() => navigate("/stock/base-de-datos/recetas")}
                sx={{ minWidth: { xs: "100%", sm: 154 }, height: 38, borderRadius: "33px", textTransform: "none", fontSize: 12, fontWeight: 800 }}
              >
                Biblioteca de recetas
              </Button>
              <Button
                type="button"
                onClick={() =>
                  navigate("/stock/base-de-datos/productos-de-venta/crear", {
                    state: { listState },
                  })
                }
                sx={{
                  minWidth: { xs: "100%", sm: 152 },
                  height: 38,
                  px: 2.4,
                  borderRadius: "33px",
                  bgcolor: "primary.500",
                  color: "logoblanco",
                  textTransform: "none",
                  fontSize: 12,
                  fontWeight: 800,
                  boxShadow: "0px 4px 12px rgba(23, 31, 76, 0.18)",
                  "&:hover": { bgcolor: "primary.600" },
                }}
              >
                Agregar producto
              </Button>
            </Stack>
          </Stack>

          {!!error && (
            <Box sx={{ px: 2, py: 1.2, borderRadius: "18px", bgcolor: "secondary.700" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                No se pudo completar la operación
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco" }}>
                {error}
              </Typography>
            </Box>
          )}

          {!!operationError && !deactivatingProduct && (
            <Box sx={{ px: 2, py: 1.2, borderRadius: "18px", bgcolor: "secondary.700" }}>
              <Typography sx={{ fontSize: 13, fontWeight: 800, color: "logoblanco" }}>
                No se pudo actualizar el producto
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "logoblanco" }}>
                {operationError}
              </Typography>
            </Box>
          )}

          <SaleProductsTable
            rows={products}
            loading={loading}
            onView={setContentProduct}
            onEdit={(product) =>
              navigate(
                `/stock/base-de-datos/productos-de-venta/${product.id}/editar`,
                { state: { listState } }
              )
            }
            onReview={(product) =>
              navigate(
                `/stock/base-de-datos/productos-de-venta/${product.id}/editar`,
                { state: { reviewMode: true, listState } }
              )
            }
            onToggleActive={requestActiveChange}
            onDelete={requestDelete}
            updatingProductId={updatingProductId}
            deletingProductId={deletingProductId}
            emptyMessage={
              hasFilters
                ? "No se encontraron productos con los filtros seleccionados."
                : "Todavía no hay productos de venta cargados."
            }
          />

          <SaleProductContentModal
            open={Boolean(contentProduct)}
            product={contentProduct}
            onClose={() => setContentProduct(null)}
          />

          <SaleProductActiveDialog
            open={Boolean(deactivatingProduct)}
            product={deactivatingProduct}
            saving={updatingProductId === deactivatingProduct?.id}
            error={deactivatingProduct ? operationError : ""}
            onClose={() => {
              setDeactivatingProduct(null);
              setOperationError("");
            }}
            onConfirm={() => changeActive(deactivatingProduct, false)}
          />

          <SaleProductDeleteDialog
            open={Boolean(productToDelete)}
            product={productToDelete}
            deleting={deletingProductId === productToDelete?.id}
            error={deleteError}
            onClose={() => {
              setProductToDelete(null);
              setDeleteError("");
            }}
            onConfirm={confirmDelete}
          />

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.2}
            sx={{ alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between", pt: 0.5 }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900" }}>
              {pageSummary}
            </Typography>
            <Pagination
              page={page}
              count={Math.max(totalPages, 1)}
              onChange={(_, value) => setPage(value)}
              shape="rounded"
              color="primary"
              disabled={loading || totalPages <= 1}
            />
          </Stack>
        </Stack>
      </CardShell>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={4500}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setSuccessMessage("");
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSuccessMessage("")}
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Stack>
  );
}
